import './widget.css';

import { SelectCompareFn } from '@ionic/core';
import { IonSelect, IonSelectOption } from '@ionic/react';
import { FieldConfig, useField } from 'formik';
import { isEqual } from 'lodash';
import {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { IonItem } from '../../../../../components/ionic/IonItem';
import { i18nKeyType } from '../../../../../i18n';
import { getLocalizedText } from '../../../../../i18n/i18n.utils';
import { useKeyboardIsShowing } from '../../../../../theme/keyboard.hook';
import { useWidgetClasses } from '../../hook/widget.hooks';
import { WidgetLabel } from './widget-label';
import {
  PickerWidgetProps,
  WidgetRef,
  createWidgetExtension,
} from './widget.types';

type PickerInputProps<ValueType> = PickerWidgetProps & {
  placeholder?: string;
  compareWith?: string | SelectCompareFn | null | undefined;
  onChange?: (val: ValueType) => void;
};

const PickerInput = <ValueType,>(
  props: PickerInputProps<ValueType> & FieldConfig
): ReactElement => {
  const {
    label,
    description,
    showDescription,
    disabled,
    options,
    placeholder,
    compareWith,
    ionItemProps,
    extensions,
    onChange,
    subtitle,
    subtitleColor,
    isNested,
    isPaywalled,
    transparentBackground,
    ...otherProps
  } = props;

  const { className: ionItemClassName, ...restIonItemProps } =
    ionItemProps ?? {};

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [field, meta, helper] = useField(otherProps as any);
  const inputRef = useRef<HTMLIonSelectElement>(null);
  const [isDisabled, setIsDisabled] = useState(disabled ?? false);
  const isKeyboardShowing = useKeyboardIsShowing();
  const pickerCompareWith = compareWith ?? isEqual;

  const rightElement = useMemo(() => {
    return createWidgetExtension(extensions?.rightElement, {
      field: [field, meta, helper],
      widgetRef: inputRef.current as WidgetRef,
      setDisabled: setIsDisabled,
      isDisabled,
    });
  }, [extensions?.rightElement, field, helper, isDisabled, meta]);

  useEffect(() => {
    if (disabled !== undefined) {
      setIsDisabled(disabled);
    }
  }, [disabled]);

  const classList = useWidgetClasses({
    isNested,
    isRippleUtilized: true,
    transparentBackground,
    isPaywalled,
  });

  const ionLabelClass = isNested ? 'ion-padding-start' : undefined;

  const onChangeInteral = useCallback(
    (val: ValueType) => {
      helper.setValue(val);

      if (typeof onChange === 'function') {
        onChange(val);
      }
    },
    [helper, onChange]
  );

  return (
    <IonItem
      className={`${classList} ${ionItemClassName || ''}`.trim()}
      lines="full"
      disabled={isDisabled}
      onTouchStart={() => {
        if (isKeyboardShowing) {
          const el = document.activeElement as HTMLInputElement;
          if (typeof el?.blur === 'function') {
            el?.blur();
          }
        }
      }}
      {...restIonItemProps}
    >
      <IonSelect
        aria-label={label}
        data-cy={otherProps.name}
        justify="space-between"
        ref={inputRef}
        disabled={isDisabled}
        interface={'popover'}
        value={field.value}
        placeholder={placeholder}
        onIonChange={(e) => {
          if (isPaywalled) {
            return;
          }

          onChangeInteral(e.detail.value);
        }}
        compareWith={pickerCompareWith}
      >
        <div slot={'label'}>
          <WidgetLabel
            label={label}
            description={description}
            showDescription={showDescription}
            error={meta.error}
            forceDisable={isDisabled}
            subtitle={subtitle}
            subtitleColor={subtitleColor}
            ionLabelProps={{
              className: ionLabelClass,
            }}
          />
        </div>
        {options.map((option, index) => {
          return (
            <IonSelectOption key={index} value={option.value}>
              {getLocalizedText(option.label as i18nKeyType)}
            </IonSelectOption>
          );
        })}
      </IonSelect>
      {rightElement}
    </IonItem>
  );
};

export { PickerInput };
